import React, { useState, useMemo, useEffect, useContext } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Divider,
  Grid,
  Stack,
  InputAdornment,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as TreeExpandIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  AccountBalance as BankIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import AdvancedSettings from "./AdvancedSettings";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HouseIcon from "@mui/icons-material/House";
import AssuredWorkloadRoundedIcon from "@mui/icons-material/AssuredWorkloadRounded";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import {
  mobileFieldProps,
  sanitizeMobileInput,
} from "../PawnShop/pawnCustomer/shared";
import { useAccountsNav } from "../../utils/moduleNav";
import {
  PAWN,
  PawnAccountsSkin,
  PawnAccountsHeader,
  pawnScrollSx,
} from "./pawnAccountsTheme";

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

axiosInstance.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage =
      error.response?.data?.message || error.message || "An error occurred";
    console.error("API Error:", errorMessage);
    return Promise.reject(error);
  },
);

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================
const accountAPI = {
  getAllAccounts: async (storeId, filters = {}) => {
    try {
      const response = await axiosInstance.get(
        `/api/accounts/accounts/${storeId}`,
        { params: filters },
      );
      return {
        success: true,
        data: response.data.data,
        count: response.data.count,
        fy_year: response.data.fy_year,
        fy_id: response.data.fy_id,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: [],
      };
    }
  },

  createAccount: async (storeId, payload) => {
    try {
      const response = await axiosInstance.post(
        `/api/accounts/accounts/${storeId}`,
        payload,
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to create account",
      };
    }
  },

  updateAccount: async (storeId, accountId, payload) => {
    try {
      const response = await axiosInstance.put(
        `/api/accounts/accounts/${storeId}/${accountId}`,
        payload,
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to update account",
      };
    }
  },

  // FIX 1: deleteAccount was called in confirmDelete but was never defined.
  deleteAccount: async (storeId, accountId) => {
    try {
      const response = await axiosInstance.delete(
        `/api/accounts/accounts/${storeId}/${accountId}`,
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        message: "Failed to delete account",
      };
    }
  },
};
// The financial year is a property of the BRANCH, not of the app. Both calls
// below therefore carry the store and the branch the session is in: without
// them the server has nothing to attribute the choice to, which is how the
// picker used to end up flipping one global flag that every store shared.
//
// A null branchId is legitimate — the service roles are in no branch — and the
// server resolves it to the store's main branch.
const financialYearAPI = {
  getAll: async (storeId, branchId) => {
    try {
      const response = await axiosInstance.get("/api/accounts/financial-year", {
        params: {
          ...(storeId !== null && storeId !== undefined
            ? { store_id: storeId }
            : {}),
          ...(branchId !== null && branchId !== undefined
            ? { branch_id: branchId }
            : {}),
        },
      });
      return {
        success: true,
        data: response.data.data,
        // The year this branch is working in, or null if it has never chosen.
        currentFyId: response.data.current_fy_id ?? null,
        // The year the calendar is in — the fallback for a branch that has not
        // chosen. Derived server-side from the date; it replaced the is_active
        // column, which tried to be both of these at once and so was reliably
        // wrong about one of them.
        calendarFyId: response.data.calendar_fy_id ?? null,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
        data: [],
        currentFyId: null,
        calendarFyId: null,
      };
    }
  },

  setActive: async (fyId, storeId, branchId) => {
    try {
      const response = await axiosInstance.patch(
        `/api/accounts/financial-year/${fyId}/set-active`,
        { store_id: storeId, branch_id: branchId ?? null },
      );
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
const mapCategoryToDBCategory = (uiCategory, parentAccount) => {
  const parentMapping = {
    assets: "ASSET",
    liabilities: "LIABILITY",
    income: "INCOME",
    expenses: "EXPENSE",
  };

  if (parentMapping[parentAccount]) {
    return parentMapping[parentAccount];
  }

  const categoryMapping = {
    "Cash / Bank / Ledger": "ASSET",
    "Fixed Assets": "ASSET",
    Inventory: "ASSET",
    Receivables: "ASSET",
    Payables: "LIABILITY",
  };

  return categoryMapping[uiCategory] || "ASSET";
};

// ── Real bank details ("Is Bank A/C") ───────────────────────────────────────
const bankPayload = (formData) => {
  const on = Boolean(formData.isBankAccount);
  const clean = (v) => (on && v && String(v).trim() ? String(v).trim() : null);
  return {
    bank_name: clean(formData.bankName),
    account_number: clean(formData.accountNumber),
    ifsc_code: clean(formData.ifscCode)?.toUpperCase() ?? null,
    phone_country: on ? formData.phoneCountry || "+91" : null,
    phone_number: clean(formData.phoneNumber),
    state_code: clean(formData.stateCode),
    pincode: clean(formData.pincode),
  };
};

// { field: message } for the bank block; empty when it is fine (or unticked).
// Bank Name, Account No and IFSC identify the bank, so they are required; the
// rest are checked only when filled. The server re-checks the formats.
const validateBankFields = (formData) => {
  if (!formData.isBankAccount) return {};
  const errors = {};
  const v = (k) => String(formData[k] || "").trim();
  if (!v("bankName")) errors.bankName = "Bank Name is required";
  if (!v("accountNumber")) errors.accountNumber = "Account No is required";
  else if (!/^[A-Za-z0-9]{6,34}$/.test(v("accountNumber")))
    errors.accountNumber = "6–34 letters/digits";
  if (!v("ifscCode")) errors.ifscCode = "IFSC Code is required";
  else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(v("ifscCode").toUpperCase()))
    errors.ifscCode = "Format: ABCD0123456";
  const phoneLen = formData.phoneCountry === "+91" || !formData.phoneCountry ? 10 : null;
  if (v("phoneNumber")) {
    if (phoneLen && v("phoneNumber").length !== phoneLen)
      errors.phoneNumber = `Must be ${phoneLen} digits`;
    else if (!phoneLen && !/^\d{6,15}$/.test(v("phoneNumber")))
      errors.phoneNumber = "6–15 digits";
  }
  if (v("pincode") && !/^\d{6}$/.test(v("pincode")))
    errors.pincode = "Must be 6 digits";
  return errors;
};

const createAccountPayload = (formData, fyYear) => {
  let parent_account_id = null;
  if (
    formData.parentAccount &&
    !["root", "income", "liabilities", "expenses", "assets"].includes(
      formData.parentAccount,
    )
  ) {
    parent_account_id = parseInt(formData.parentAccount);
  }

  const advSettings = formData.advancedSettings || {};

  return {
    account_name: formData.accountName,
    account_code: formData.accountCode || null,
    parent_account_id,
    account_type: formData.accountType === "group" ? "GROUP" : "ACCOUNT",
    account_category: mapCategoryToDBCategory(
      formData.accountCategory,
      formData.parentAccount,
    ),
    opening_balance: parseFloat(formData.openingBalance) || 0.0,
    opening_balance_type: formData.balanceType || "DEBIT",
    is_bank_account: formData.isBankAccount || false,
    // Stored in account_bank_details (one row per bank account). Sent as null
    // when the box is unticked — the server then drops the bank row.
    ...bankPayload(formData),
    fy_year: fyYear,

    show_daybook_details:
      advSettings.show_daybook_details !== undefined
        ? advSettings.show_daybook_details
        : true,
    report_order: advSettings.report_order || 999,
    gst_applicable:
      advSettings.gst_applicable !== undefined
        ? advSettings.gst_applicable
        : false,
    hsn_sac_code: advSettings.hsn_sac_code || null,
    type_of_supply: advSettings.type_of_supply || null,
    interest_enabled:
      advSettings.interest_enabled !== undefined
        ? advSettings.interest_enabled
        : false,
    interest_rate: parseFloat(advSettings.interest_rate) || 0.0,
    interest_type: advSettings.interest_type || "Simple",
    interest_from: advSettings.interest_from || "Due Date",
    inventory_values_affected:
      advSettings.inventory_values_affected !== undefined
        ? advSettings.inventory_values_affected
        : false,
    enable_cheque_tracking:
      advSettings.enable_cheque_tracking !== undefined
        ? advSettings.enable_cheque_tracking
        : false,
    enable_bank_reconciliation:
      advSettings.enable_bank_reconciliation !== undefined
        ? advSettings.enable_bank_reconciliation
        : false,
    lock_for_posting:
      advSettings.lock_for_posting !== undefined
        ? advSettings.lock_for_posting
        : false,

    is_active: formData.isActive !== undefined ? formData.isActive : true,
  };
};

const buildAccountTree = (accounts) => {
  const accountMap = new Map();

  const masterGroups = {
    INCOME: {
      id: "income",
      name: "Income",
      children: [],
      isGroup: true,
      isMasterGroup: true,
      category: "INCOME",
    },
    LIABILITY: {
      id: "liabilities",
      name: "Liabilities",
      children: [],
      isGroup: true,
      isMasterGroup: true,
      category: "LIABILITY",
    },
    EXPENSE: {
      id: "expenses",
      name: "Expenses",
      children: [],
      isGroup: true,
      isMasterGroup: true,
      category: "EXPENSE",
    },
    ASSET: {
      id: "assets",
      name: "Assets",
      children: [],
      isGroup: true,
      isMasterGroup: true,
      category: "ASSET",
    },
  };

  accounts.forEach((account) => {
    accountMap.set(account.account_id, {
      id: account.account_id.toString(),
      name: account.account_name,
      accountName: account.account_name,
      accountCode: account.account_code,
      isGroup: account.account_type === "GROUP",
      parentAccount: account.parent_account_id
        ? account.parent_account_id.toString()
        : null,

      openingBalance:
        account.fy_opening_balance !== undefined
          ? account.fy_opening_balance
          : account.opening_balance,
      balanceType:
        account.fy_opening_type !== undefined
          ? account.fy_opening_type
          : account.opening_balance_type,

      fyOpeningBalance: account.fy_opening_balance,
      fyOpeningType: account.fy_opening_type,
      fyTotalDebit: account.fy_total_debit,
      fyTotalCredit: account.fy_total_credit,
      fyClosingBalance: account.fy_closing_balance,
      fyClosingType: account.fy_closing_type,

      accountCategory: account.account_category,
      isBankAccount: account.is_bank_account,
      bankName: account.bank_name || "",
      accountNumber: account.account_number || "",
      ifscCode: account.ifsc_code || "",
      stateCode: account.state_code || "",
      pincode: account.pincode || "",
      phoneCountry: account.phone_country || "+91",
      phoneNumber: account.phone_number || "",
      isActive: account.is_active,
      children: [],

      advancedSettings: {
        show_daybook_details: account.show_daybook_details,
        report_order: account.report_order,
        gst_applicable: account.gst_applicable,
        hsn_sac_code: account.hsn_sac_code,
        type_of_supply: account.type_of_supply,
        interest_enabled: account.interest_enabled,
        interest_rate: account.interest_rate,
        interest_type: account.interest_type,
        interest_from: account.interest_from,
        inventory_values_affected: account.inventory_values_affected,
        enable_cheque_tracking: account.enable_cheque_tracking,
        enable_bank_reconciliation: account.enable_bank_reconciliation,
        lock_for_posting: account.lock_for_posting,
      },
    });
  });

  accounts.forEach((account) => {
    const node = accountMap.get(account.account_id);

    if (account.parent_account_id === null) {
      const masterGroup = masterGroups[account.account_category];
      if (masterGroup) {
        masterGroup.children.push(node);
      }
    } else {
      const parent = accountMap.get(account.parent_account_id);
      if (parent) {
        parent.children.push(node);
      }
    }
  });

  return {
    id: "root",
    name: "All Accounts",
    children: [
      masterGroups.INCOME,
      masterGroups.LIABILITY,
      masterGroups.EXPENSE,
      masterGroups.ASSET,
    ],
  };
};

const findNodeById = (tree, id) => {
  if (tree.id === id) return tree;
  if (tree.children) {
    for (const child of tree.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
};

const addChildToNode = (tree, parentId, newChild) => {
  const newTree = JSON.parse(JSON.stringify(tree));
  const addChild = (node) => {
    if (node.id === parentId) {
      if (!node.children) node.children = [];
      node.children.push(newChild);
      return true;
    }
    if (node.children) {
      for (let child of node.children) {
        if (addChild(child)) return true;
      }
    }
    return false;
  };
  addChild(newTree);
  return newTree;
};

const updateNodeById = (tree, nodeId, updates) => {
  const newTree = JSON.parse(JSON.stringify(tree));
  const updateNode = (node) => {
    if (node.id === nodeId) {
      Object.assign(node, updates);
      return true;
    }
    if (node.children) {
      for (let child of node.children) {
        if (updateNode(child)) return true;
      }
    }
    return false;
  };
  updateNode(newTree);
  return newTree;
};

const deleteNodeById = (tree, nodeId) => {
  const newTree = JSON.parse(JSON.stringify(tree));
  const removeNode = (node) => {
    if (node.children) {
      node.children = node.children.filter((child) => {
        if (child.id === nodeId) return false;
        removeNode(child);
        return true;
      });
    }
  };
  removeNode(newTree);
  return newTree;
};

const flattenTree = (node, level = 0, result = []) => {
  if (node.id !== "root" && node.isGroup) {
    result.push({ id: node.id, name: node.name, level });
  }
  if (node.children) {
    node.children.forEach((child) => flattenTree(child, level + 1, result));
  }
  return result;
};

const filterTree = (node, query) => {
  if (!query || query.trim() === "") return node;
  const lowerQuery = query.toLowerCase().trim();

  const filterNode = (n) => {
    const nameMatch = n.name?.toLowerCase().includes(lowerQuery);
    const filteredChildren = (n.children || []).map(filterNode).filter(Boolean);
    if (nameMatch || filteredChildren.length > 0) {
      return { ...n, children: filteredChildren };
    }
    return null;
  };

  const filtered = filterNode(node);
  return filtered || { ...node, children: [] };
};

const collectExpandableIds = (node, result = []) => {
  if (node.children && node.children.length > 0) {
    result.push(node.id);
    node.children.forEach((child) => collectExpandableIds(child, result));
  }
  return result;
};

// ============================================================================
// CONSTANTS
// ============================================================================
const ACCOUNT_CATEGORIES = [
  "Cash / Bank / Ledger",
  "Fixed Assets",
  "Inventory",
  "Receivables",
  "Payables",
];

const BALANCE_TYPES = ["CREDIT", "DEBIT"];
const MASTER_GROUP_IDS = ["income", "liabilities", "expenses", "assets"];

const DEFAULT_NEW_ACCOUNT_DATA = {
  id: `new-${Date.now()}`,
  accountName: "",
  accountCode: "",
  parentAccount: "",
  accountType: "account",
  openingBalance: "0.00",
  balanceType: "DEBIT",
  accountCategory: "",
  isBankAccount: false,
  phoneCountry: "+91",
  phoneNumber: "",
  isActive: true,
  advancedSettings: {
    show_daybook_details: true,
    report_order: 999,
    gst_applicable: false,
    hsn_sac_code: "",
    type_of_supply: "Services",
    interest_enabled: false,
    interest_rate: 0.0,
    interest_type: "Simple",
    interest_from: "Due Date",
    inventory_values_affected: false,
    enable_cheque_tracking: false,
    enable_bank_reconciliation: false,
    lock_for_posting: false,
  },
};

// ============================================================================
// COMPONENTS
// ============================================================================

// The tree is the one place with hard-coded (Travel-ERP purple/amber) colours
// that a theme override can't reach, so the palette is picked per host module.
const TREE_SKIN = {
  default: {
    selectedBg: "#f3e5f5",
    hoverBg: "#fafafa",
    accountIcon: "#7c4dff",
    groupIcon: "#ff9800",
    selectedInk: "inherit",
  },
  pawn: {
    selectedBg: PAWN.BLUE_PALE,
    hoverBg: PAWN.HEAD_BG,
    accountIcon: PAWN.BLUE,
    groupIcon: PAWN.INK_FAINT,
    selectedInk: PAWN.BLUE_INK,
  },
};

const CustomTreeItem = ({
  node,
  level,
  selectedAccount,
  expanded,
  onToggle,
  onSelect,
  skin = TREE_SKIN.default,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.includes(node.id);
  const isSelected = selectedAccount === node.id;

  const handleClick = () => {
    if (hasChildren) onToggle(node.id);
    onSelect(node.id);
  };

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        sx={{
          pl: level * 1.5,
          py: 0.4,
          minHeight: 32,
          backgroundColor: isSelected ? skin.selectedBg : "transparent",
          borderRadius: 0.5,
          "&:hover": {
            backgroundColor: isSelected ? skin.selectedBg : skin.hoverBg,
          },
        }}
      >
        {hasChildren && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            {isExpanded ? (
              <TreeExpandIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </ListItemIcon>
        )}
        {!hasChildren && !node.isGroup && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            <BankIcon sx={{ fontSize: 14, color: skin.accountIcon }} />
          </ListItemIcon>
        )}
        {!hasChildren && node.isGroup && (
          <ListItemIcon sx={{ minWidth: 24 }}>
            <FolderIcon sx={{ fontSize: 14, color: skin.groupIcon }} />
          </ListItemIcon>
        )}
        <ListItemText
          primary={node.name}
          primaryTypographyProps={{
            variant: "body2",
            fontSize: "0.8rem",
            fontWeight: node.isGroup ? 600 : 400,
            color: isSelected ? skin.selectedInk : "inherit",
          }}
        />
      </ListItemButton>
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <CustomTreeItem
                key={child.id}
                node={child}
                level={level + 1}
                selectedAccount={selectedAccount}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelect}
                skin={skin}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const AccountTree = ({
  treeData,
  selectedAccount,
  onSelectAccount,
  onNewAccount,
  onRefresh,
  isLoading,
  expanded,
  onToggle,
  searchQuery,
  onSearchChange,
  pawn = false,
}) => {
  const skin = pawn ? TREE_SKIN.pawn : TREE_SKIN.default;

  return (
    <Card
      sx={{
        height: "100%",
        overflow: "auto",
        ...(pawn ? pawnScrollSx : {}),
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            fullWidth
            onClick={onNewAccount}
            size="small"
            sx={{ textTransform: "none", py: 0.5 }}
          >
            New Account
          </Button>
          <Button
            variant="outlined"
            startIcon={
              isLoading ? <CircularProgress size={14} /> : <RefreshIcon />
            }
            fullWidth
            onClick={onRefresh}
            disabled={isLoading}
            size="small"
            sx={{ textTransform: "none", py: 0.5 }}
          >
            Refresh
          </Button>

          <TextField
            placeholder="Search accounts or groups..."
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange("")}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
          />
        </Stack>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List component="nav" disablePadding dense>
            {treeData.children.map((child) => (
              <CustomTreeItem
                key={child.id}
                node={child}
                level={0}
                selectedAccount={selectedAccount}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelectAccount}
                skin={skin}
              />
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

const AccountForm = ({
  formData,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isDirty,
  isNewAccount,
  parentAccounts,
  isSaving,
  bankErrors = {},
}) => {
  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    onChange({ ...formData, [field]: value });
  };

  // Bank fields refuse what can never be valid as it is typed or pasted.
  const phoneMaxDigits = (formData.phoneCountry || "+91") === "+91" ? 10 : 15;
  const handleBankInput = (field, clean) => (event) =>
    onChange({ ...formData, [field]: clean(event.target.value) });
  const bankErrorProps = (field) =>
    bankErrors[field]
      ? { error: true, helperText: bankErrors[field] }
      : {};

  const [showAccountInfoFields, setShowAccountInfoFields] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleOpeningBalanceChange = (event) => {
    onChange({ ...formData, openingBalance: event.target.value });
  };

  const handleOpeningBalanceBlur = () => {
    if (formData.openingBalance === "" || formData.openingBalance === null) {
      onChange({ ...formData, openingBalance: "0.00" });
    }
  };

  return (
    <Card>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        {formData.accountName && !isNewAccount && (
          <Alert
            severity="error"
            sx={{
              mb: 1.5,
              py: 0.5,
              backgroundColor: "rgba(241, 16, 0, 0.32)",
              border: "1px solid rgba(244, 67, 54, 0.25)",
              color: "#b71c1c",
              "& .MuiAlert-icon": { color: "#c20d00" },
            }}
          >
            <Typography variant="body2" fontSize="0.9rem">
              Editing: {formData.accountName}
            </Typography>
          </Alert>
        )}

        {isNewAccount && (
          <Alert
            severity="success"
            sx={{
              mb: 1.5,
              py: 0.5,
              backgroundColor: "rgba(18, 187, 24, 0.22)",
              border: "1px solid rgba(76, 175, 80, 0.25)",
              color: "#15741c",
              "& .MuiAlert-icon": { color: "#007704" },
            }}
          >
            <Typography variant="body2" fontSize="0.9rem">
              Creating New Account/Group
            </Typography>
          </Alert>
        )}

        <Typography variant="h6" fontSize="1rem" fontWeight={600} gutterBottom>
          Account Details
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            sx={{ fontSize: "0.85rem" }}
          >
            Basic Information
          </Typography>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={8}>
              <TextField
                label="Account Name"
                required
                fullWidth
                value={formData.accountName || ""}
                onChange={handleChange("accountName")}
                size="small"
                disabled={isSaving}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Account Code"
                fullWidth
                value={formData.accountCode || ""}
                onChange={handleChange("accountCode")}
                size="small"
                disabled={isSaving}
                placeholder="Optional"
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={parentAccounts}
                getOptionLabel={(option) => option.name}
                value={
                  parentAccounts.find(
                    (acc) => acc.id === formData.parentAccount,
                  ) || null
                }
                onChange={(event, newValue) => {
                  onChange({
                    ...formData,
                    parentAccount: newValue ? newValue.id : "",
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Account"
                    size="small"
                    required
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      paddingLeft: option.level * 20,
                      fontSize: "0.85rem",
                    }}
                  >
                    {option.name}
                  </li>
                )}
                disabled={isSaving}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                disabled={isSaving}
                size="small"
              >
                <FormLabel
                  component="legend"
                  sx={{ fontSize: "0.85rem", mb: 0.5 }}
                >
                  Account Type
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.accountType || "account"}
                  onChange={handleChange("accountType")}
                >
                  <FormControlLabel
                    value="account"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" fontSize="0.85rem">
                        Account
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="group"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" fontSize="0.85rem">
                        Group
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={8}>
              <TextField
                label="Opening Balance"
                type="number"
                fullWidth
                value={formData.openingBalance || ""}
                onChange={handleOpeningBalanceChange}
                onBlur={handleOpeningBalanceBlur}
                size="small"
                disabled={isSaving}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.balanceType || "DEBIT"}
                  onChange={handleChange("balanceType")}
                  disabled={isSaving}
                >
                  {BALANCE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Typography variant="body2" fontSize="0.85rem">
                        {type}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            sx={{ fontSize: "0.85rem" }}
          >
            Account Classification
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Autocomplete
                options={ACCOUNT_CATEGORIES}
                value={formData.accountCategory || null}
                onChange={(event, newValue) => {
                  onChange({ ...formData, accountCategory: newValue || "" });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Account Category"
                    size="small"
                  />
                )}
                disabled={isSaving}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formData.isBankAccount)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      handleChange("isBankAccount")(e);
                      setShowAccountInfoFields(checked);
                    }}
                    disabled={isSaving}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontSize="0.85rem">
                    Is Bank A/C
                  </Typography>
                }
              />
            </Grid>

            {formData.isBankAccount && (
              <Grid item xs={12} sx={{ mt: -1 }}>
                <Collapse
                  in={Boolean(formData.isBankAccount)}
                  timeout={350}
                  easing={{
                    enter: "cubic-bezier(0.4, 0, 0.2, 1)",
                    exit: "cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                  collapsedSize={0}
                  unmountOnExit
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Bank Name"
                        required
                        fullWidth
                        size="small"
                        value={formData.bankName || ""}
                        onChange={handleChange("bankName")}
                        disabled={isSaving}
                        inputProps={{ maxLength: 150 }}
                        {...bankErrorProps("bankName")}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Account No"
                        required
                        fullWidth
                        size="small"
                        value={formData.accountNumber || ""}
                        onChange={handleBankInput("accountNumber", (v) =>
                          v.replace(/[^A-Za-z0-9]/g, "").slice(0, 34),
                        )}
                        disabled={isSaving}
                        inputProps={{ autoComplete: "off" }}
                        {...bankErrorProps("accountNumber")}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="IFSC Code"
                        required
                        fullWidth
                        size="small"
                        value={formData.ifscCode || ""}
                        onChange={handleBankInput("ifscCode", (v) =>
                          v
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, "")
                            .slice(0, 11),
                        )}
                        disabled={isSaving}
                        inputProps={{ autoComplete: "off" }}
                        {...bankErrorProps("ifscCode")}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Phone"
                        fullWidth
                        size="small"
                        value={formData.phoneNumber || ""}
                        onChange={handleBankInput("phoneNumber", (v) =>
                          sanitizeMobileInput(v, phoneMaxDigits),
                        )}
                        disabled={isSaving}
                        {...mobileFieldProps(phoneMaxDigits)}
                        {...bankErrorProps("phoneNumber")}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Select
                                value={formData.phoneCountry || "+91"}
                                onChange={handleChange("phoneCountry")}
                                variant="standard"
                                disableUnderline
                                disabled={isSaving}
                                sx={{ fontSize: "0.85rem" }}
                              >
                                <MenuItem value="+91">
                                  <Typography fontSize="0.85rem">
                                    +91
                                  </Typography>
                                </MenuItem>
                                <MenuItem value="+1">
                                  <Typography fontSize="0.85rem">+1</Typography>
                                </MenuItem>
                                <MenuItem value="+44">
                                  <Typography fontSize="0.85rem">
                                    +44
                                  </Typography>
                                </MenuItem>
                              </Select>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="State Code"
                        fullWidth
                        size="small"
                        value={formData.stateCode || ""}
                        onChange={handleChange("stateCode")}
                        disabled={isSaving}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Pincode"
                        fullWidth
                        size="small"
                        value={formData.pincode || ""}
                        onChange={handleBankInput("pincode", (v) =>
                          v.replace(/\D/g, "").slice(0, 6),
                        )}
                        disabled={isSaving}
                        inputProps={{ inputMode: "numeric", autoComplete: "off" }}
                        {...bankErrorProps("pincode")}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={{ mt: 1.5 }}>
          <Box
            onClick={() => setShowAdvanced((prev) => !prev)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              px: 1,
              py: 0.75,
              border: `1px solid #C2C2C2`,
              borderRadius: 0.5,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Typography fontWeight={500} fontSize="0.85rem">
              Advanced Settings
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 20,
                transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms ease",
              }}
            />
          </Box>

          <Collapse in={showAdvanced} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5 }}>
              <AdvancedSettings
                value={formData.advancedSettings || {}}
                onChange={(newAdvancedSettings) => {
                  onChange({
                    ...formData,
                    advancedSettings: newAdvancedSettings,
                  });
                }}
                disabled={isSaving}
              />
            </Box>
          </Collapse>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2 }}
          justifyContent="flex-end"
        >
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
          {!isNewAccount && (
            <Button
              onClick={onDelete}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              disabled={isSaving}
              size="small"
            >
              Delete
            </Button>
          )}
          <Button
            onClick={onSave}
            variant="contained"
            disabled={!isDirty || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
            size="small"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

// FIX 2: AccountPreviewPanel had a broken `const {}` destructure and
// referenced `defaultFY` from the outer scope (which is inaccessible inside
// a component defined at module level). It now receives `defaultFY` as a prop.
const AccountPreviewPanel = ({ formData, onClose, defaultFY }) => {
  return (
    <Card sx={{ height: "100%", position: "relative" }}>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography variant="h6" fontSize="0.95rem" fontWeight={600}>
            Account Details
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{ minHeight: 40, py: 0.5 }}
          >
            <Typography fontWeight={600} fontSize="0.85rem">
              Basic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                >
                  Account Name
                </Typography>
                <Typography variant="body2" fontSize="0.8rem">
                  {formData.accountName || "-"}
                </Typography>
              </Box>
              {formData.accountCode && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontSize="0.7rem"
                  >
                    Account Code
                  </Typography>
                  <Typography variant="body2" fontSize="0.8rem">
                    {formData.accountCode}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                >
                  Account Type
                </Typography>
                <Typography variant="body2" fontSize="0.8rem">
                  {formData.accountType === "group" ? "Group" : "Account"}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                >
                  {/* FIX 3: defaultFY is now a prop — safe to use here */}
                  Opening Balance
                  {defaultFY != null
                    ? ` (FY ${defaultFY}-${String(defaultFY + 1).slice(-2)})`
                    : ""}
                </Typography>
                <Typography variant="body2" fontSize="0.8rem">
                  {formData.openingBalance || "0.00"} {formData.balanceType}
                </Typography>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
            sx={{ minHeight: 40, py: 0.5 }}
          >
            <Typography fontWeight={600} fontSize="0.85rem">
              Account Classification
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                >
                  Account Category
                </Typography>
                <Typography variant="body2" fontSize="0.8rem">
                  {formData.accountCategory || "-"}
                </Typography>
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isBankAccount || false}
                      disabled
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontSize="0.8rem">
                      Is Bank A/C
                    </Typography>
                  }
                />
              </Box>
              {formData.isBankAccount && (
                <>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                    >
                      Bank Name
                    </Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {formData.bankName || "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                    >
                      Account No
                    </Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {formData.accountNumber || "-"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                    >
                      IFSC Code
                    </Typography>
                    <Typography variant="body2" fontSize="0.8rem">
                      {formData.ifscCode || "-"}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN LAYOUT COMPONENT
// ============================================================================
const AccountMasterLayout = () => {
  const { userInfo, defaultFY, setDefaultFY, defaultFYId, currentBranch } =
    useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;

  // The branch this session is in, which is what the financial year hangs off.
  // null is a real value, not "not loaded yet" — the service roles are in every
  // branch and so in none, and the server maps that to the store's main branch.
  // currentBranch is the authoritative copy; userInfo.branch_id is the same id
  // flattened onto the user and is read only as a fallback for the pawn screens,
  // which can render before this context has hydrated.
  const branch_id = currentBranch?.id ?? userInfo?.branch_id ?? null;
  const location = useLocation();
  const navigate = useNavigate();

  // Which module is hosting this screen (Daily Tick / Travel ERP / Pawn Shop):
  // decides the home target, keeps the sibling accounts screens inside the same
  // route tree, and switches the whole screen to the pawn skin.
  const { inPawn, go: goAccounts, goHome } = useAccountsNav();

  const [accountTree, setAccountTree] = useState({
    id: "root",
    name: "All Accounts",
    children: [
      {
        id: "income",
        name: "Income",
        children: [],
        isGroup: true,
        isMasterGroup: true,
      },
      {
        id: "liabilities",
        name: "Liabilities",
        children: [],
        isGroup: true,
        isMasterGroup: true,
      },
      {
        id: "expenses",
        name: "Expenses",
        children: [],
        isGroup: true,
        isMasterGroup: true,
      },
      {
        id: "assets",
        name: "Assets",
        children: [],
        isGroup: true,
        isMasterGroup: true,
      },
    ],
  });

  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [formData, setFormData] = useState({
    ...DEFAULT_NEW_ACCOUNT_DATA,
    id: `new-${Date.now()}`,
  });
  const [originalData, setOriginalData] = useState({
    ...DEFAULT_NEW_ACCOUNT_DATA,
    id: `new-${Date.now()}`,
  });
  const [isNewAccount, setIsNewAccount] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Red outlines on the bank fields appear only after a blocked save, then
  // track the form live until a different account is opened.
  const [showBankErrors, setShowBankErrors] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [expanded, setExpanded] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // FIX 4: Track the FY that was actually committed, so the snackbar message
  // is always accurate even if context state hasn't propagated yet.
  const [committedFY, setCommittedFY] = useState(null);
  const [fySnackbar, setFYSnackbar] = useState(false);
  const [fyOptions, setFyOptions] = useState([]); // { fy_id, fy_year, fy_label }
  const [fyLoading, setFyLoading] = useState(false);
  const isDirty = useMemo(
    () => JSON.stringify(formData) !== JSON.stringify(originalData),
    [formData, originalData],
  );

  const parentAccounts = useMemo(() => flattenTree(accountTree), [accountTree]);

  const filteredTree = useMemo(
    () => filterTree(accountTree, searchQuery),
    [accountTree, searchQuery],
  );
  // Reloads on a branch change as well as on mount: the year is per branch, so
  // moving between branches of a store has to re-ask which one this branch is
  // in. switchBranch does not reload the page, so nothing else would.
  useEffect(() => {
    loadFinancialYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store_id, branch_id]);

  const loadFinancialYears = async () => {
    setFyLoading(true);
    try {
      const result = await financialYearAPI.getAll(store_id, branch_id);
      if (result.success) {
        setFyOptions(result.data);

        // What the branch is working in, if it has chosen. This used to read
        // is_active, which meant "the year the calendar is in" and was the same
        // for every store in the database — taking it for this branch's year is
        // the bug this screen had.
        const branchFY = result.data.find(
          (fy) => fy.fy_id === result.currentFyId,
        );

        if (branchFY && setDefaultFY) {
          if (branchFY.fy_year !== defaultFY) {
            setDefaultFY(branchFY.fy_year, branchFY.fy_id);
          }
          setCommittedFY(branchFY.fy_year);
        } else if (defaultFY === null || defaultFY === undefined) {
          // This branch has never chosen one, and nothing is cached — fall back
          // to the year the calendar is in.
          const calendarFY = result.data.find(
            (fy) => fy.fy_id === result.calendarFyId,
          );
          if (calendarFY && setDefaultFY) {
            setDefaultFY(calendarFY.fy_year, calendarFY.fy_id);
            setCommittedFY(calendarFY.fy_year);
          }
        }
      } else {
        showSnackbar("Failed to load financial years", "error");
      }
    } finally {
      setFyLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === "") return;
    const allExpandableIds = collectExpandableIds(filteredTree);
    setExpanded(allExpandableIds);
  }, [searchQuery, filteredTree]);

  useEffect(() => {
    if (location.state?.createAccountName) {
      handleNewAccount();
      setFormData((prev) => ({
        ...prev,
        accountName: location.state.createAccountName,
      }));
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (
      store_id !== null &&
      store_id !== undefined &&
      defaultFY !== null &&
      defaultFY !== undefined
    ) {
      loadAccounts();
    }
  }, [store_id, defaultFY]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const handleToggle = (nodeId) => {
    setExpanded((prev) =>
      prev.includes(nodeId)
        ? prev.filter((id) => id !== nodeId)
        : [...prev, nodeId],
    );
  };

  const loadAccounts = async () => {
    if (store_id === null || store_id === undefined) {
      showSnackbar("Store ID not available", "error");
      return;
    }
    if (defaultFY === null || defaultFY === undefined) {
      showSnackbar("Please select a financial year", "error");
      return;
    }

    setIsLoading(true);
    try {
      const result = await accountAPI.getAllAccounts(store_id, {
        is_active: true,
        fy_year: defaultFY,
      });

      if (result.success && result.data.length > 0) {
        const tree = buildAccountTree(result.data);
        setAccountTree(tree);
        if (result.fy_year) {
          // console.log(
          //   `Loaded balances for FY ${result.fy_year}-${result.fy_year + 1}`,
          // );
        }
      }
    } catch (error) {
      showSnackbar("Failed to load accounts", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setExpanded([]);
    setSearchQuery("");
    await loadAccounts();
    handleCancel();
  };

  const handleNewAccount = () => {
    const newAccountData = {
      ...DEFAULT_NEW_ACCOUNT_DATA,
      id: `new-${Date.now()}`,
    };
    setIsNewAccount(true);
    setSelectedAccountId(null);
    setShowBankErrors(false);
    setFormData(newAccountData);
    setOriginalData(newAccountData);
  };

  const handleSelectAccount = (accountId) => {
    const node = findNodeById(accountTree, accountId);
    if (!node) return;

    // Map account_category to master group id when parent is null
    const categoryToMasterGroupId = {
      INCOME: "income",
      LIABILITY: "liabilities",
      EXPENSE: "expenses",
      ASSET: "assets",
    };

    const resolvedParentAccount =
      node.parentAccount !== null && node.parentAccount !== undefined
        ? node.parentAccount
        : categoryToMasterGroupId[node.accountCategory] || "";

    const accountData = {
      id: node.id,
      accountName: node.name || node.accountName || "",
      accountCode: node.accountCode || "",
      parentAccount: resolvedParentAccount, // ← only change from before
      accountType: node.isGroup ? "group" : "account",
      openingBalance:
        node.fyOpeningBalance !== undefined
          ? node.fyOpeningBalance
          : node.openingBalance || "0.00",
      balanceType:
        node.fyOpeningType !== undefined
          ? node.fyOpeningType
          : node.balanceType || "DEBIT",
      accountCategory: node.accountCategory || "",
      isBankAccount: node.isBankAccount || false,
      bankName: node.bankName || "",
      accountNumber: node.accountNumber || "",
      ifscCode: node.ifscCode || "",
      stateCode: node.stateCode || "",
      pincode: node.pincode || "",
      phoneCountry: node.phoneCountry || "+91",
      phoneNumber: node.phoneNumber || "",
      isMasterGroup: node.isMasterGroup || false,
      isActive: node.isActive !== undefined ? node.isActive : true,
      advancedSettings: node.advancedSettings || {
        show_daybook_details: true,
        report_order: 999,
        gst_applicable: false,
        hsn_sac_code: "",
        type_of_supply: "Services",
        interest_enabled: false,
        interest_rate: 0.0,
        interest_type: "Simple",
        interest_from: "Due Date",
        inventory_values_affected: false,
        enable_cheque_tracking: false,
        enable_bank_reconciliation: false,
        lock_for_posting: false,
      },
    };

    setIsNewAccount(false);
    setSelectedAccountId(accountId);
    setShowBankErrors(false);
    setFormData(accountData);
    setOriginalData(accountData);
  };

  const handleFormChange = (newData) => {
    setFormData(newData);
  };

  const handleSave = async () => {
    if (store_id === null || store_id === undefined) {
      showSnackbar("Store ID not available", "error");
      return;
    }
    if (!formData.accountName || !formData.parentAccount) {
      showSnackbar(
        "Please fill in required fields: Account Name and Parent Account",
        "error",
      );
      return;
    }
    if (defaultFY === null || defaultFY === undefined) {
      showSnackbar("Please select a financial year", "error");
      return;
    }
    if (formData.isMasterGroup && !isNewAccount) {
      showSnackbar(
        "Master groups (Income, Liabilities, Expenses, Assets) cannot be modified.",
        "error",
      );
      return;
    }
    const bankFieldErrors = validateBankFields(formData);
    if (Object.keys(bankFieldErrors).length > 0) {
      setShowBankErrors(true);
      showSnackbar(Object.values(bankFieldErrors)[0], "error");
      return;
    }

    setIsSaving(true);
    try {
      const payload = createAccountPayload(formData, defaultFY);

      if (isNewAccount) {
        const result = await accountAPI.createAccount(store_id, payload);
        if (result.success) {
          await loadAccounts();
          showSnackbar(result.message, "success");
          if (location.state?.returnPath) {
            navigate(location.state.returnPath, {
              state: {
                newAccount: {
                  account_id: result.data.account_id,
                  account_name: result.data.account_name,
                  account_code: result.data.account_code,
                },
              },
            });
          } else {
            handleNewAccount();
          }
        } else {
          showSnackbar(result.error || result.message, "error");
        }
      } else {
        const result = await accountAPI.updateAccount(
          store_id,
          formData.id,
          payload,
        );
        if (result.success) {
          await loadAccounts();
          showSnackbar(result.message, "success");
          handleNewAccount();
        } else {
          showSnackbar(result.error || result.message, "error");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      showSnackbar("An unexpected error occurred", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (location.state?.returnPath) {
      navigate(location.state.returnPath);
    } else {
      handleNewAccount();
    }
  };

  const handleDelete = () => {
    if (MASTER_GROUP_IDS.includes(selectedAccountId)) {
      showSnackbar(
        "Master groups (Income, Liabilities, Expenses, Assets) cannot be deleted.",
        "error",
      );
      return;
    }
    setDeleteDialogOpen(true);
  };

  // FIX 5: setDefaultFY from context is a plain React state setter — it is NOT
  // async. Awaiting it does nothing and can silently swallow errors. We call it
  // synchronously and capture the chosen value locally for the snackbar message
  // so the displayed year is always the one the user just selected, regardless
  // of when the context re-renders.
  const handleFYChange = async (e) => {
    const selectedFyYear = e.target.value; // fy_year value from Select

    if (isDirty) {
      const confirmed = window.confirm(
        "You have unsaved changes. Changing the financial year will discard them. Continue?",
      );
      if (!confirmed) return;
    }

    // Find the fy_id for this fy_year
    const selected = fyOptions.find((fy) => fy.fy_year === selectedFyYear);
    if (!selected) return;

    // Nothing to attribute the choice to without a store, and the server will
    // refuse it — say so here rather than letting it read as a network failure.
    if (store_id === null || store_id === undefined) {
      showSnackbar("Store not available — cannot set the financial year", "error");
      return;
    }

    // Persists onto branch_master.current_fy for the branch this session is in,
    // so it survives a reload and does not leak into any other branch or store.
    const result = await financialYearAPI.setActive(
      selected.fy_id,
      store_id,
      branch_id,
    );
    if (!result.success) {
      showSnackbar(
        result.error || "Failed to update the financial year",
        "error",
      );
      return;
    }

    setDefaultFY(selectedFyYear, selected.fy_id); // ← adds fy_id
    setCommittedFY(selectedFyYear);
    setFYSnackbar(true);
    handleNewAccount();
  };

  const confirmDelete = async () => {
    if (store_id === null || store_id === undefined) {
      showSnackbar("Store ID not available", "error");
      return;
    }

    setIsSaving(true);
    try {
      const result = await accountAPI.deleteAccount(
        store_id,
        selectedAccountId,
      );
      if (result.success) {
        const updatedTree = deleteNodeById(accountTree, selectedAccountId);
        setAccountTree(updatedTree);
        setDeleteDialogOpen(false);
        setSelectedAccountId(null);
        setFormData({});
        setOriginalData({});
        showSnackbar(result.message, "success");
      } else {
        showSnackbar(result.error || result.message, "error");
      }
    } catch (error) {
      console.error("Delete error:", error);
      showSnackbar("An unexpected error occurred", "error");
    } finally {
      setIsSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  // The FY picker and the cross-links to the sibling accounts screens are the
  // same controls in both hosts — only the chrome around them differs, so they
  // are built once and dropped into whichever header renders.
  const fyPicker = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: inPawn ? PAWN.BORDER : "primary.main",
        borderRadius: 1.5,
        backgroundColor: inPawn ? "#FFFFFF" : "#f0f7ff",
      }}
    >
      <Box
        sx={{
          whiteSpace: "nowrap",
          width: 14,
          height: 14,
          borderRadius: 100,
          bgcolor: "rgb(25, 207, 25)",
        }}
      />
      {fyLoading ? (
        <CircularProgress size={14} />
      ) : (
        <Select
          value={defaultFY ?? ""}
          onChange={handleFYChange}
          size="small"
          variant="standard"
          disableUnderline
          sx={{
            fontSize: "0.82rem",
            fontWeight: 700,
            color: "primary.main",
            minWidth: 80,
            "& .MuiSelect-select": { py: 0 },
          }}
        >
          {fyOptions.map((fy) => (
            <MenuItem key={fy.fy_id} value={fy.fy_year}>
              <Typography fontSize="0.82rem" fontWeight={600}>
                FY {fy.fy_label}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );

  // goAccounts() keeps these inside the host's route tree — /accounts-ledger on
  // Daily Tick & Travel, /pawn/accounts-ledger inside the pawn module.
  const crossLinks = (
    <>
      <Button
        onClick={() => goAccounts("/accounts-ledger")}
        variant="outlined"
        color="primary"
        size={inPawn ? "small" : "medium"}
      >
        Ledger
      </Button>
      <Button
        onClick={() => goAccounts("/accounts-cost-center")}
        variant="outlined"
        color="primary"
        size={inPawn ? "small" : "medium"}
      >
        Cost Center
      </Button>
      <Button
        onClick={() => goAccounts("/accounts-entry")}
        variant="outlined"
        color="primary"
        size={inPawn ? "small" : "medium"}
      >
        Account Entry
      </Button>
    </>
  );

  // Held in a const so wrapping it in the pawn skin below doesn't re-indent the
  // whole tree.
  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        ...(inPawn && { p: { xs: 1.5, md: 2.5 } }),
      }}
    >
      {inPawn ? (
        <PawnAccountsHeader
          icon={<AssuredWorkloadRoundedIcon />}
          title="Account Master"
          subtitle={
            defaultFY
              ? `Cloud Accounting · FY ${defaultFY}-${defaultFY + 1}`
              : "Cloud Accounting"
          }
          actions={
            <>
              {fyPicker}
              {crossLinks}
            </>
          }
        />
      ) : (
        <Box
          sx={{
            bgcolor: "white",
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
            py: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box
              onClick={() => navigate(-1)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: 2,
                bgcolor: "#139fcae8",
                p: 0.5,
              }}
            >
              <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
            </Box>
            <Box
              onClick={goHome}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: 2,
                bgcolor: "#139fcae8",
                p: 0.5,
              }}
            >
              <HouseIcon sx={{ fill: "#FFF" }} />
            </Box>
            <Typography variant="h6" fontSize="1.1rem" fontWeight={600}>
              Cloud Accounting – Account Master{" "}
              <span style={{ fontSize: "1rem" }}>
                {`(${defaultFY} - ${defaultFY + 1})`}{" "}
              </span>
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {fyPicker}
            {crossLinks}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          p: inPawn ? 0 : 1.5,
          gap: 1.5,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: 260, flexShrink: 0 }}>
          <AccountTree
            treeData={filteredTree}
            selectedAccount={selectedAccountId}
            onSelectAccount={handleSelectAccount}
            onNewAccount={handleNewAccount}
            onRefresh={handleRefresh}
            isLoading={isLoading}
            expanded={expanded}
            onToggle={handleToggle}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            pawn={inPawn}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c0c0c0",
              borderRadius: "3px",
            },
          }}
        >
          {selectedAccountId || isNewAccount ? (
            <AccountForm
              formData={formData}
              onChange={handleFormChange}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={handleDelete}
              isDirty={isDirty}
              isNewAccount={isNewAccount}
              parentAccounts={parentAccounts}
              isSaving={isSaving}
              bankErrors={
                showBankErrors ? validateBankFields(formData) : undefined
              }
            />
          ) : (
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 300,
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    gutterBottom
                    fontSize="1rem"
                  >
                    No Account Selected
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontSize="0.85rem"
                  >
                    Select an account from the tree or create a new one
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>

        {showPreview && (selectedAccountId || isNewAccount) && (
          <Box sx={{ width: 280, flexShrink: 0 }}>
            {/* FIX 6: Pass defaultFY as a prop so AccountPreviewPanel can
                display the correct FY label without closing over a stale value. */}
            <AccountPreviewPanel
              formData={formData}
              onClose={() => setShowPreview(false)}
              defaultFY={defaultFY}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSaving && setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ fontSize: "1rem" }}>Delete Account</DialogTitle>
        <DialogContent>
          <Typography fontSize="0.9rem">
            Are you sure you want to delete "{formData.accountName}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isSaving}
            size="small"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : null}
            size="small"
          >
            {isSaving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* FIX 7: Use committedFY (captured at the moment of change) instead of
          defaultFY (which may still reflect the old value while context
          re-renders), so the snackbar always shows the newly selected FY. */}
      <Snackbar
        open={fySnackbar}
        autoHideDuration={2500}
        onClose={() => setFYSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFYSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Active Financial Year updated to FY{" "}
          {committedFY != null
            ? `${committedFY}-${String(committedFY + 1).slice(-2)}`
            : ""}
        </Alert>
      </Snackbar>
    </Box>
  );

  return <PawnAccountsSkin active={inPawn}>{content}</PawnAccountsSkin>;
};

export default AccountMasterLayout;
